import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { ImagePreviewWrapperSizeEnum } from 'shared/ui/ImagePreviewWrapper/ImagePreviewWrapper'

import { AppTableMobile } from 'components/AppTable/AppTableMobile'
import { IChooseItems } from 'core/api/dto/BuffetDishDto'
import { AppTableImageViewer } from 'components/AppTableImageViewer/AppTableImageViewer'
import { useDeleteAiDish } from '../hooks/useDeleteAiDish'
import DishAiImagePopup from './DishAiImagePopup'
import { mapComplianceToLabel, mapSourceToLabel } from 'utils/complianceHelpers'
import { AppDeletePopup } from 'entities/AppDeletePopup/AppDeletePopup'
import moment from 'moment'
import { useGetStandardAiDatasetListQuery } from '../../../core/api/StandardAiDataset'
import { IStandardAiDatasetDto } from 'core/api/dto/StandardAiDatasetDto'
import { AppTableLink } from 'components/AppTable/components/AppTableLink'
import { useGetMismatchTypeListQuery } from 'core/api/TypeMismatchApi'
import { AppLoader, LoaderType } from 'components/AppLoader'

export enum DetailTab {
  'main' = 'main',
  'info' = 'info',
}

export const InfoTab = () => {
  const { dishId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const [file, setFile] = useState<null | File>(null)
  const [filePreview, setFilePreview] = useState<null | string>(null)
  const limit = Number(searchParams.get('limit')) || 40

  const queryParams = useMemo(() => {
    const defaultOrdering = 'is_archived'
    const ordering = searchParams.get('ordering') || defaultOrdering
    return {
      ordering,
      dish_id: dishId,
      is_archived: false,
      limit,
    }
  }, [searchParams])

  const {
    data: standardDatasets,
    isLoading,
    isFetching,
  } = useGetStandardAiDatasetListQuery(queryParams)

  const [dishImagePopup, setDishImagePopup] = useState<{ active: boolean }>({
    active: false,
  })

  const { data: mismatchTypeList } = useGetMismatchTypeListQuery(
    {
      is_archived: false,
    },
    { skip: !dishImagePopup.active },
  )

  const mismatchOptions = useMemo(
    () =>
      mismatchTypeList?.results
        ? mismatchTypeList.results.map((r) => ({ id: r.id, name: r.name }))
        : [],
    [mismatchTypeList],
  )

  const currentImageId = useMemo(() => {
    const param = searchParams.get('id')
    return param ? Number(param) : null
  }, [searchParams])

  useEffect(() => {
    if (currentImageId !== null && !dishImagePopup.active) {
      setDishImagePopup({ active: true })
    } else if (currentImageId === null && dishImagePopup.active) {
      setDishImagePopup({ active: false })
    }
  }, [currentImageId])

  const openDishImagePopup = (
    photo: string,
    name?: string,
    imageId?: number,
  ) => {
    if (imageId !== undefined && imageId !== null) {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('id', String(imageId))
      setSearchParams(newParams, { replace: true })
    }
    setDishImagePopup({ active: true })
  }

  const closeDishImagePopup = () => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('id')
    newParams.delete('mismatchType')
    setSearchParams(newParams, { replace: true })
    setDishImagePopup({ active: false })
  }

  useEffect(() => {
    if (!!file) {
      setFilePreview(URL.createObjectURL(file))
    }
  }, [file])

  const imagesTableDataSelectors = [
    {
      renderItem: (item: IStandardAiDatasetDto) => (
        <AppTableImageViewer
          imgPreview={item.dish.imagePreviewOnS3 || item.dish.imagePreview}
          image={item.imagePreview || item.imageOnS3 || ''}
          imageName={item.dish?.name || ''}
          archived={item.isArchived}
          size={ImagePreviewWrapperSizeEnum.MEDIUM}
          onImageClick={(photo, title) =>
            openDishImagePopup(photo, title, item.id)
          }
        />
      ),
    },
    {
      renderItem: (item: IStandardAiDatasetDto) => (
        <AppTableLink path={`${item.imageOnS3}`}>
          {item.imageOnS3 || item.image || '-'}
        </AppTableLink>
      ),
    },
    {
      renderItem: (item: IStandardAiDatasetDto) => (
        <div>{mapSourceToLabel(item.uploadedFrom) || '-'}</div>
      ),
    },
    {
      renderItem: (item: IStandardAiDatasetDto) => {
        const { label, className } = mapComplianceToLabel(
          item.complianceWithStandards,
        )
        return <div className={`table-status ${className}`}>{label}</div>
      },
    },
    {
      renderItem: (item: IStandardAiDatasetDto) => {
        const complianceStatus = item.complianceWithStandards
        if (complianceStatus === 'Not_determined') {
          return <div>-</div>
        }
        return (
          <div>
            {item.nonCompliantTypes?.join(', ').toLocaleLowerCase() || ''}
          </div>
        )
      },
    },
    {
      renderItem: (item: IStandardAiDatasetDto) => (
        <div>{moment.parseZone(item.createdAt).format('DD.MM.YYYY, HH:mm')}</div>
      ),
    },
  ]

  const headerData = [
    { title: 'Фото', colWidth: '5%' },
    { title: 'Ссылка', colWidth: '35%', sort: 'image' },
    { title: 'Источник', colWidth: '13%', sort: 'uploaded_from' },
    { title: 'Статус', colWidth: '12%', sort: 'compliance_with_standards' },
    {
      title: 'Тип несоответствия',
      colWidth: '15%',
      sort: 'non_compliant_types',
    },
    { title: 'Дата и время', colWidth: '12%', sort: 'created_at' },
  ]

  const [chooseItems, setChooseItems] = useState<IChooseItems | null>({})
  const [tableData, setTableData] = useState([])

  const {
    deletePopup,
    onDeletePopupHandler,
    onDeleteHandler,
    closeDeletePopup,
  } = useDeleteAiDish(chooseItems, setChooseItems)

  const tableActions = [
    {
      title: 'Удалить',
      ico: 'basket',
      onClick: onDeletePopupHandler,
    },
  ]

  if (isLoading) {
    return <AppLoader loaderType={LoaderType.aiTable} />
  }

  if (!standardDatasets) return null

  return (
    <>
      <div className="tab-content">
        <AppTableMobile
          data={standardDatasets}
          headerData={headerData}
          chooseActions={tableActions}
          chooseMode
          chooseItems={chooseItems}
          setChooseItems={setChooseItems}
          setTableData={setTableData as any}
          idName={'id'}
          tableDataSelectors={imagesTableDataSelectors}
          tableClassName="dish-ai "
          isLoading={isFetching}
        />
      </div>
      {deletePopup && (
        <AppDeletePopup onClose={closeDeletePopup} onDelete={onDeleteHandler} />
      )}
      {dishImagePopup.active && standardDatasets && (
        <DishAiImagePopup
          active={dishImagePopup.active}
          onClose={closeDishImagePopup}
          reportImagePopupData={standardDatasets.results}
          mismatchOptions={mismatchOptions}
        />
      )}
    </>
  )
}
